import elementResizeEvent from 'element-resize-event';
import jQuery from 'jquery';
import Backbone from 'backbone';
import _ from 'underscore';

import GoogleMapsUtils from 'helpers/GoogleMapsUtils';
import EventBus from './widgetHelpers/EventBus';
import WidgetCommon from './WidgetCommon';
import HandleLayers from 'layers/HandleLayers';
import enableControlsAwareCentering from 'helpers/enableControlsAwareCentering';
import elementQuery from 'libs/elementQuery';
import assertValidConfig from 'helpers/assertValidConfig';

function controlBBox(widget, limitedBBox) {
  google.maps.event.addListener(widget.map, 'center_changed', () => {
    GoogleMapsUtils.setLimitsToBBox(widget.map, limitedBBox);
  });
}

function setControlBBox(widget) {
  let limitedBBox = widget.map.getBounds();
  widget.map.minZoom = widget.map.getZoom();
  if (_.isObject(widget.config.defaultView.limitBounds)) {
    limitedBBox = GoogleMapsUtils.getBounds(widget.config.defaultView.limitBounds);
  } else if (widget.config.defaultView.bounds) {
    limitedBBox = GoogleMapsUtils.getBounds(widget.config.defaultView.bounds);
  }
  controlBBox(widget, limitedBBox);
}

function addCustomServices(widget, configServices) {
  let Services = widget.serviceClasses;
  _.each(configServices, (service, key) => {
    widget.customServices[key] = new Services[key](_.extend(
      service, {
        map: widget.map,
        widget: widget,
        config: service
      }));
  });
}

function isMobile(widget) {
  let breakpoints = widget.config.breakpoints,
    defaultBreakpoint = 720,
    widgetWidth = widget.$el.width();

  if (breakpoints.maxTablet) {
    defaultBreakpoint = parseFloat(breakpoints.maxTablet.replace('px', ''));
  } else if (breakpoints.maxMobile) {
    defaultBreakpoint = parseFloat(breakpoints.maxMobile.replace('px', ''));
  }
  return defaultBreakpoint >= widgetWidth;
}

function changeScreenSize(widget) {
  let isMobileNow = isMobile(widget);
  if (widget.isMobile !== isMobileNow) {
    widget.bus.trigger('CHANGE_SCREEN_SIZE', isMobileNow);
  }
  widget.isMobile = isMobileNow;
}

function addMapListeners(widget) {
  google.maps.event.addListenerOnce(widget.map, 'bounds_changed', () => {
    let Components = require('components/').default;
    WidgetCommon.addCustomComponents(widget, widget.config.mapComponents, Components);
    if (widget.config.defaultView.limitBounds) {
      setControlBBox(widget);
    }
    widget.bus.trigger('WIDGET_INITIALIZED', widget.map.getBounds());
  });

  google.maps.event.addDomListener(widget.map, 'resize', () => {
    setTimeout(() => {
      widget.map.panTo(widget._lastCenter);
      changeScreenSize(widget);
    }, 1);
  });

  google.maps.event.addDomListener(widget.map, 'dragend', () => {
    widget.bus.trigger('USER_ACTION_TRACKING', {
      tracking: 'MapCenterChanged',
      boundingBox: widget.map.getBounds()
    });
  });

  google.maps.event.addDomListener(widget.map, 'zoom_changed', () => {
    widget.bus.trigger('USER_ACTION_TRACKING', {
      tracking: 'ZoomChanged',
      zoom: widget.map.getZoom()
    });
  });

  // We keep the last known center of the map in order to restore it in window or container resize.
  google.maps.event.addDomListener(widget.map, 'idle', function() {
    widget._lastCenter = widget.map.getCenter();
  });

  widget._lastCenter = widget.map.getCenter();
}

function resizeMapHandler(widget) {
  let element = widget.$el.find('.map-container')[0];

  // We trigger resize each time the container div changes its size.
  elementResizeEvent(element, () => {
    google.maps.event.trigger(widget.map, 'resize');
  });
}

function centerMapHandler(widget) {
  widget.listenTo(widget.bus, 'CENTER_MAP', (center) => {
    widget.map.panTo(center);
  });
}

function _getGoogleMapsControls(config) {
  let controlOptions = {};
  let components = _.filter(config.mapComponents, function(component) {
    return (_.indexOf(GoogleMapsUtils.googleControls, component.componentConstructorClass) > -1);
  });

  _.each(components, (component) => {
    controlOptions[component.componentConstructorClass] = true;
    // Other Options
    var options = _.extend({}, component.options);
    // Control Position
    if (component.position) {
      options.position = google.maps.ControlPosition[component.position];
    }
    // Control Style
    if (component.style && component.style.control && component.style.controlStyle) {
      options.style = google.maps[component.style.control][component.style.controlStyle];
    }
    // Control MapIds
    if (component.mapTypeIds) {
      options.mapTypeIds = component.mapTypeIds;
    }
    controlOptions[component.nameOptions] = options;
  });

  return controlOptions;
}

function getMapOptions(widget) {
  let config = widget.config;
  let controlOptions = _getGoogleMapsControls(config);
  let mapOptions = _.extend({
    minZoom: 2,
    zoomControl: false,
    mapTypeControl: false,
    panControl: false,
    scaleControl: false,
    streetViewControl: false,
    overviewMapControl: false,
    fullscreenControl: false
  });

  if (!_.isObject(config.mapOptions)) {
    config.mapOptions = {};
  }
  _.extend(mapOptions, config.mapOptions, controlOptions);

  if (!config.defaultView) {
    config.defaultView = {};
  }

  if (_.isArray(config.defaultView.center) && !_.isEmpty(config.defaultView.center)) {
    let center = config.defaultView.center;
    mapOptions.center = new google.maps.LatLng(center[0], center[1]);
    mapOptions.zoom = config.defaultView.zoom;
  } else {
    let _bbox = widget._initialBBox = _.isObject(config.defaultView.bounds) ? config.defaultView.bounds : {
        neLat: 83.31110306468723,
        neLng: 180,
        swLat: -67.76757057057642,
        swLng: -180
      },
      bbox = GoogleMapsUtils.getBounds(_bbox);
    mapOptions.center = GoogleMapsUtils.getBoundsCenter(bbox);
  }

  return mapOptions;
}

function renderMap(widget) {
  let mapOptions = getMapOptions(widget);
  let mapContainer = widget.$el.find('.map-container')[0];
  /**
   *  A Google Map event
   *  @typedef {object} mapEvent
   */

  /**
   * Event fired when map zoom changes
   * @type {mapEvent}
   * @event Widget#zoom_changed
   */
  /**
   * Event fired when the map becomes idle after panning or zooming.
   * @type {mapEvent}
   * @event Widget#idle
   */
  widget.map = new google.maps.Map(mapContainer, mapOptions);

  /**
   * @name handleLayers
   * @desc The instance of HandleLayers used by the widget so components can get layers by id or create layers and stuff.
   * @type {core.widget.widgetHelpers.HandleLayers}
   * @memberof core.widget.Widget.prototype
   * @protected
   */
  widget.handleLayers = new HandleLayers(widget);

  if (widget.config.controlsAwareMapCentering) {
    // Replaces vainilla fitBounds  and setCenter functions with substitutes that take into account
    // the controls placed on the map.
    enableControlsAwareCentering.init(widget.map);
  }

  if (widget._initialBBox) {
    let bbox = GoogleMapsUtils.getBounds(widget._initialBBox);
    widget.map.fitBounds(bbox);
  }
  addMapListeners(widget);
  resizeMapHandler(widget);
  centerMapHandler(widget);
}

function _setBreakpoints(breakpoints) {
  let containerId = '.lmm-maps-widget-container',
    _params = {};

  _params[containerId] = {
    'max-width': breakpoints.maxTablet
  };
  elementQuery(_params, true);
}

function overwritteNavigationPosition(position) {
  navigator.geolocation.getCurrentPosition = function(success, failure) {
    success({
      coords: {
        latitude: position.lat,
        longitude: position.lng
      },
      timestamp: Date.now()
    });
  };
}

/**
 * The api object from integrationAPI
 * @typedef APIObject
 * @desc Contains methods that will be called for the widget to interact with the web page
 * embedding it. It is highly dependend on the project and must be implemented by the widget integrator.
 * @memberof core
 * @example <caption>API object example</caption>
 * var apiObject = {
 *  userAction: function(data){
 *    //do something
 *  },
 *  search: function(params){
 *    //do something
 *  },
 *  notify: function(params){
 *    //do something
 *  }
 * }
 */

const Widget = Backbone.View.extend(
  /**
   * @lends core.widget.Widget.prototype
   */
  {

    /**
     * It is a template function used to generate the widgets's DOM, normally generated by requiring a Handlebars template and
     * letting the appropiate Webpack loader convert it to a function.
     *
     * Widget extending classes may provide their own templates.
     * @protected
     * @function
     * @param {object} data The data to be used to be referenced in the template to generate the DOM.
     */
    template: require('./widget.hbs'),

    /**
     * The data to be passed to the template function when it gets rendered.
     * @protected
     */
    templateConfig: {},

    /**
     * Validation function, normally generated by requiring an JSON schema file and
     * letting Webpack compile the proper validaiton function.
     * Override this to allow for adding custom configuration properties to the class extending Widget.
     * @function
     * @param {core.Config} config
     * @protected
     */
    validation: require('./widget.schema.json'),

    /**
     * @desc The configuration object received by the widget, for use in components and layers that
     *  need to read directly from the config.
     * @protected
     * @type {core.Config}
     */
    config: null,

    /**
     * @desc The evnet bus instance used by components and layers to communicante using messages.
     * @type {core.widget.widgetHelpers.EventBus}
     * @protected
     */
    bus: null,

    /**
     * @desc Allows creating messages. The way that the messages are shown must be defined in a component that uses
     * this property as model
     * @type {core.widget.widgetHelpers.MessageControlCollection}
     */
    messageControl: null,

    /**
     * @desc The product layers created from the config's "productLayer" entry for this widget.
     * @type {core.layers.BaseLayer[]}
     * @protected
     */
    productLayers: null,

    /**
     * @desc The non product layers created from the config's "layers" property for this widget.
     * @type {core.layers.BaseLayer[]}
     * @protected
     */
    layers: null,

    /**
     * The registered component constructor functions that can be loaded through config.
     *
     * Should be added calling to registerDependencies' registerComponent function parameter.
     * @protected
     * @type {Object<string, function>}
     */
    componentClasses: {},

    /**
     * The registered layer constructor functions that can be loaded through config.
     *
     * Should be added calling to registerConfigClasses' registerLayer function parameter.
     * @protected
     * @type {Object<string, function>}
     */
    layerClasses: {},

    /**
     * The registered layer provider constructor functions that can be loaded through config.
     *
     * Should be added calling to registerConfigClasses' registerLayerProvider function parameter.
     * @protected
     * @type {Object<string, function>}
     */
    layerProviderClasses: {},

    /**
     * The registered Service constructor functions that can be loaded through config.
     *
     * Should be added calling to registerConfigClasses' registerService function received as parameter.
     * @protected
     * @type {Object<string, function>}
     */
    serviceClasses: {},

    /**
     * @type {google.maps.Map}
     * @desc the Google Maps instance used by the widget, for use in components, layers and other widget receiving classes.
     * @protected
     * @see https://developers.google.com/maps/documentation/javascript/reference#Map
     */
    map: null,

    /**
     * Create a widget instance.
     * @class Widget
     * @classdesc Base class so projects can create and define their own configurations
     * @abstract
     * @memberof core.widget
     * @param {string} containerId - The div id where the widget will append.
     * @param {core.Config} config - The config provided by JSON.
     * Extend this class when extending Widget to add project related configuration.
     * @param {core.APIObject} api - The object the widget will use to interact with the portal.
     * @throws {Error} If the container id parameter is falsy or is not the id of an existing DOM element;
     * or if the config parameter is not a valid configuration.
     */
    constructor(containerId, config, api) {

      if (!containerId) {
        throw new Error(Widget.NO_CONTAINERID_ERR);
      }

      let container = jQuery('#' + containerId);
      if (container.length !== 1) {
        throw new Error('Widget::constructor: containerId must be the id of an existing DOM element');
      }

      // We set the widget's config in the validation function, so we activate validation,
      // and with it the loading of the validation bundle.
      assertValidConfig.doValidation = config.doValidation;

      assertValidConfig(`widget '${containerId}'`, this.validation, config);

      this.config = WidgetCommon.prepareConfig(config, this);

      WidgetCommon.registerHelpers(this);
      this.customComponents = {};
      this.customServices = {};

      this.bus = new EventBus().bus;
      this.handleLayers = null;

      this.api = api;
      this.setElement(container, true);

      this.notify = this.portalInteraction;

      Backbone.View.prototype.constructor.call(this);
    },

    /**
     * Initialize the widget
     * @private
     */
    initialize() {
      // This allows setting the screen resolutions and load different
      // css resources for different devices
      if (this.config.screenResolution) {
        this.templateConfig = { className: '_' + this.config.screenResolution };
      }

      // The inheriting Widget class must specify which components will by available to be created through the config.
      this.registerConfigClasses((componentClassName, componentConstructor) => {
        this.componentClasses[componentClassName] = componentConstructor;
      }, (layerClassName, layerConstructor) => {
        this.layerClasses[layerClassName] = layerConstructor;
      }, (layerProviderClassName, layerProviderConstructor) => {
        this.layerProviderClasses[layerProviderClassName] = layerProviderConstructor;
      }, (serviceClassName, serviceConstructor) => {
        this.serviceClasses[serviceClassName] = serviceConstructor;
      });

      WidgetCommon.setLocale(this).done(() => {

        if (!this.config.breakpoints) {
          this.config.breakpoints = {};
        }
        _setBreakpoints(this.config.breakpoints);
        this.isMobile = isMobile(this);

        renderMap(this.render());

        // Adds productLayers and layers properties.
        _.extend(this, this.handleLayers.createConfigLayers(
          this.config.layerDefaults,
          this.config.productLayers,
          this.config.layers));

        addCustomServices(this, this.config.services);

        WidgetCommon.createMessageControl(this);
        if (this.config.fakeLocation) {
          overwritteNavigationPosition(this.config.fakeLocation);
        }

        this.onInitialized(this.config);
      });
    },

    /**
     * Registers the dependencies of the widget. The extending class MUST override this method
     * in order to make the classes of dependencies to be created by interpreting configurations, i.e.
     * the widget's or a component's.
     *
     * @protected
     * @abstract
     * @param {function} registerComponent The function used to register component classes.
     * @param {function} registerLayer The function used to register layer classes to the widget.
     * @param {function} registerLayerProvider The function used to register layer provider classes.
     * @param {function} registerService The function used to register a service instantiable using the widget's config.
     */
    registerConfigClasses: function(registerComponent, registerLayer, registerLayerProvider, registerService) {
      throw new Error(Widget.OVERRIDE_REGISTERCONFIGCLASSES_ERR);
    },

    /**
     * Method to be overriden in derivate classes in order to, for example, add layers, components
     * or any other initialization.
     *
     * @protected
     * @abstract
     * @param {core.Config} config The widget's configuration
     */
    onInitialized(config) {
      throw new Error(Widget.OVERRIDE_ONINITIALIZE_ERR);
    },

    /**
     * Makes the widget to stop listening events and destroys all resources.
     */
    destroy: function() {
      this.stopListening();
      google.maps.event.clearInstanceListeners(this.map);
      this.map = null;
      // TODO: Destroy components (recursively) and layers.
    },

    /**
     * Adds a component to the widget. To be called by derivate classes to programatically add classes.
     * @protected
     * @param {core.components.base.ComponentBase} component
     * @throws {Error} If the received parameter is falsy.
     */
    addComponent(component) {
      if (!component) {
        throw new Error('Widget::addComponent: The component parameter cannot be falsy');
      }
      WidgetCommon.registerComponent(this, component);
    },

    /**
     * Adds a layer to the map. To be called by derivate classes to programatically add layers.
     * @protected
     * @param {core.layers.BaseLayer} layer The layer to be added to the map.
     * @throws {Error} If the layer is falsy or if it doesn't have an 'id' property.
     */
    addLayer(layer) {
      if (!layer) {
        throw new Error('Widget::addLayer: the layer cannot be null');
      }
      if (!layer.id) {
        throw new Error('Widget::addLayer: the layer must have an "id" property');
      }
      this.handleLayers.addToMap(layer);
    },

    /**
     * Render the template. Override if you want to change the way the widget is rendered.
     * @override
     * @protected
     * @param {Object} tmpObj - The new template data
     * @returns {core.widget.Widget} The rendered widget.
     */
    render: function(tmpObj) {
      if (tmpObj) {
        this.templateConfig = _.extend({}, this.templateConfig, tmpObj);
      }
      this.$el.html(this.template(this.templateConfig));
      return this;
    },

    /**
     * @typedef {object} WidgetCommand
     * @property {string} type The name of the event
     * @property {any} object The payload of the event
     * @memberof core.widget.Widget
     */

    /**
     * Function used for communications from portal to widget.
     * @alias notify
     * @memberof core.widget.Widget.prototype
     * @param {core.widget.Widget.WidgetCommand} params The command sent from portal for
     * the widget to execute
     */
    portalInteraction: function(params) {
      this.bus.trigger(params.type, params.object);
    },

    /**
     * @typedef {object} UserAction
     * @desc A user action reported by the Widget to the portal trough the received API object, if any.
     * Might contain more properties but at least the tracking property must be present.
     * @property {string} tracking The name of the tracked value.
     * @memberof core.widget.Widget
     * @see core.widget.Widget.APIObject
     */

    /**
     * Called by components to notify the portal that the user did some action.
     * Only has effect if the widget received an API object containing a userAction function.
     * @param {UserAction} userAction The action to be reported to the portal.
     * @throws {Error} If the parameters doesn't  contain a tracking property.
     */
    trackUserAction(userAction) {
      if (!this.api || !this.api.userAction) {
        return;
      }

      if (!userAction.tracking) {
        throw new Error(
          'Wiget::trackUserAction requires a tracking property in the received action!');
      }

      // Timeout to prevent too much reporting.
      if (!this._trackingTimeouts) {
        this._trackingTimeouts = {};
      }

      if (this._trackingTimeouts[userAction.tracking]) {
        // We cancel the current timeout for the event.
        clearTimeout(this._trackingTimeouts[userAction.tracking]);
      }

      let timeout = setTimeout(() => {
        this.api.userAction(userAction);
      }, 5);
      this._trackingTimeouts[userAction.tracking] = timeout;
    }
  });

Widget.NO_CONTAINERID_ERR = 'Widget::constructor: containerId is a required parameter';
Widget.OVERRIDE_ONINITIALIZE_ERR = 'Widget::onInitialized: This method needs to be implemented in derivate classes';
Widget.OVERRIDE_REGISTERCONFIGCLASSES_ERR = 'Widget::registerConfigClasses: must be overriden in derivate classes';

export default Widget;



// WEBPACK FOOTER //
// ./lmm-widget/src/widget/Widget.js